import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/** Tailwind 클래스 병합 헬퍼 — shadcn 표준 패턴. */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** 숫자 천단위 콤마. null/undefined 는 '-'. */
export function fmt(n: number | null | undefined): string {
  if (n === null || n === undefined) return "-";
  return n.toLocaleString("ko-KR");
}

/** ms → '4.5s' 또는 '450ms' 표시. */
export function fmtMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return "-";
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;
}

/** ISO 문자열 → 'YYYY-MM-DD HH:mm:ss' (KST 가정). */
export function fmtKst(iso: string): string {
  const d = new Date(iso);
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${yy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
}

/** bytes → '1.2MB' / '340KB' 표시. 알 수 없으면 빈 문자열. */
export function fmtBytes(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n) || n < 0) return "";
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)}KB`;
  return `${(n / (1024 * 1024)).toFixed(1)}MB`;
}

interface FileLike {
  role?: string | null;
  name?: string;
  size?: number | null;
  mime?: string | null;
  download_token?: string | null;
}

/** files_json 중 사용자가 보낸 첨부(role=input)만.
 *  role 미기록 구버전 로그는 download_token 유무로 입력/산출물을 구분한다. */
export function inputFiles<T extends FileLike>(files: T[] | undefined | null): T[] {
  return (files ?? []).filter((f) => {
    const role = f.role || (f.download_token ? "output" : "input");
    return role === "input" && !!f.name;
  });
}

/** files_json 중 산출물(role=output 또는 download_token 보유). */
export function outputFiles<T extends FileLike>(files: T[] | undefined | null): T[] {
  return (files ?? []).filter((f) => {
    const role = f.role || (f.download_token ? "output" : "input");
    return role === "output" && !!f.name;
  });
}

/** 질문란 표시 문구 — 빈 질문/구버전 '(파일만)' 로그는 첨부 파일명으로 대체.
 *  (첨부만 보낸 요청이 "(파일만)" 으로만 보이던 문제 보정) */
export function questionText(
  question: string | null | undefined,
  files?: FileLike[] | null,
): string {
  const q = (question ?? "").trim();
  if (q && q !== "(파일만)" && q !== "(없음)") return q;
  const names = inputFiles(files).map((f) => f.name as string);
  if (names.length === 0) return q || "(요청 문구 없음)";
  const head = names.slice(0, 3).join(", ");
  const rest = names.length - Math.min(names.length, 3);
  return `[파일 요청] ${head}${rest > 0 ? ` 외 ${rest}건` : ""}`;
}

/** LLM 모델 ID → 짧은 표시명.
 *  예) 'claude-sonnet-4-6' → 'Sonnet 4.6', 'claude-haiku-4-5-20251001' → 'Haiku 4.5'.
 *  규칙에 안 맞으면 원문 그대로, null/빈 값은 '-' 반환.
 */
export function fmtModel(model: string | null | undefined): string {
  if (!model) return "-";
  const m = model.trim();
  if (!m || m === "(미기록)") return m || "-";
  const parts = m.replace(/^claude-/, "").split("-");
  const family = parts[0];
  if (!family || /^\d/.test(family)) return m;
  // 숫자 토큰만 버전으로 결합 (8자리 날짜 스냅샷은 제외)
  const ver = parts
    .slice(1)
    .filter((t) => /^\d+[a-z]?$/i.test(t) && t.length < 8)
    .join(".");
  const label = family.charAt(0).toUpperCase() + family.slice(1);
  return ver ? `${label} ${ver}` : label;
}
